import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from './ui/dialog';
import { Bug, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { submitBugReport } from '../lib/api';

export const BugReportModal = ({ open, onOpenChange }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        subject: '',
        description: '',
        steps_to_reproduce: '',
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.subject.trim() || !formData.description.trim()) {
            toast.error('Please fill in the subject and description');
            return;
        }

        setLoading(true);
        try {
            const browserInfo = `${navigator.userAgent} | ${window.innerWidth}x${window.innerHeight}`;
            await submitBugReport({
                subject: formData.subject,
                description: formData.description,
                steps: formData.steps_to_reproduce || undefined,
                browser_info: browserInfo,
            });
            toast.success('Bug report submitted! Thank you for your feedback.');
            setFormData({ subject: '', description: '', steps_to_reproduce: '' });
            onOpenChange(false);
        } catch (err) {
            toast.error('Failed to submit bug report. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Bug className="w-5 h-5 text-purple-600" />
                        Report a Bug
                    </DialogTitle>
                    <DialogDescription>
                        Found something wrong? Let us know and we'll fix it as soon as possible.
                    </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                            id="subject"
                            name="subject"
                            placeholder="Brief description of the issue"
                            value={formData.subject}
                            onChange={handleChange}
                            disabled={loading}
                            data-testid="bug-subject-input"
                        />
                    </div>
                    
                    <div className="space-y-2">
                        <Label htmlFor="description">Description *</Label>
                        <Textarea
                            id="description"
                            name="description"
                            placeholder="What happened? What did you expect to happen?"
                            rows={4}
                            value={formData.description}
                            onChange={handleChange}
                            disabled={loading}
                            data-testid="bug-description-input"
                        />
                    </div>
                    
                    <div className="space-y-2">
                        <Label htmlFor="steps_to_reproduce">Steps to Reproduce (Optional)</Label>
                        <Textarea
                            id="steps_to_reproduce"
                            name="steps_to_reproduce"
                            placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                            rows={3}
                            value={formData.steps_to_reproduce}
                            onChange={handleChange}
                            disabled={loading}
                            data-testid="bug-steps-input"
                        />
                    </div>
                    
                    <DialogFooter>
                        <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => onOpenChange(false)}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            disabled={loading}
                            data-testid="submit-bug-report-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4 mr-2" />
                                    Submit Report
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
